/**
 * Config declarativa de um recurso de cadastro. Consumida por:
 *  - ResourceListPage (lista + busca + filtros + excluir + navegar p/ form)
 *  - ResourceFormPage (tela dedicada de criar/editar — sem modal)
 *
 * Cada recurso exporta um objeto `ResourceConfig<T>`; as rotas de lista/`new`/
 * `:uuid` são registradas no AppRoutes reaproveitando o mesmo config.
 */
import type { ReactElement, ReactNode } from 'react'
import type { ZodType } from 'zod'
import type { FieldValues } from 'react-hook-form'
import type { Column } from '../../shared/components/DataTable'
import type { Resource, ListParams } from '../../core/api/resource'
import type { ProtheusEntidade } from '../../shared/protheus/ProtheusSyncButton'

export interface ResourceFilters {
  node?: ReactNode
  params?: ListParams
}

export interface ResourceConfig<T extends { uuid: string }> {
  title: string
  eyebrow?: string
  singular: string
  /** Base da rota, ex.: '/logistica/cadastros/places'. */
  basePath: string
  labelOf: (row: T) => string
  resource: Resource<T>
  columns: Column<T>[]
  searchable?: boolean
  schema: ZodType
  emptyForm: FieldValues
  toForm: (row: T) => FieldValues
  toBody: (values: FieldValues) => Record<string, unknown>
  /** Campos do formulário — é um COMPONENTE (pode usar hooks p/ buscar opções). */
  Fields: (props: { editing: boolean }) => ReactElement
  /** Card paralelo opcional ao lado do formulário (ex.: mapa do local). Renderizado
   *  dentro do FormProvider → pode useWatch os valores ao vivo. */
  Aside?: (props: { editing: boolean }) => ReactElement | null
  /** Card opcional em LARGURA TOTAL abaixo do formulário (ex.: mapa do local). Como o
   *  Aside, roda dentro do FormProvider (pode useWatch/setValue ao vivo), mas ocupa a
   *  linha inteira em vez do trilho lateral. Quando presente, os botões Salvar/Cancelar
   *  descem para um rodapé após este card. */
  Below?: (props: { editing: boolean }) => ReactElement | null
  formMaxWidth?: number
  /** Filtros da lista dirigidos por hook (categoria/escopo/contexto). Opcional. */
  useFilters?: () => ResourceFilters
  /** Se setado, mostra "Nova categoria" ao lado de "Novo" (navega p/ este path). */
  categoryNewPath?: string
  /** Se setado, mostra o botão "Importar" (importação em massa — Mudanças v1 §3)
   *  com a entidade correspondente do backend (/imports/entities/<key>). */
  importEntity?: string
  /** Cadastros do Protheus que esta grade mostra. Com isto, o botão de folha de
   *  sincronizar agora aparece ao lado de Novo e Importar e dispara só esses. */
  protheusSync?: ProtheusEntidade[]
  /** Prefixo do arquivo baixado por "Baixar Excel" (ex.: 'frota' → frota_2026-09-02.xlsx).
   *  Presente, a grade ganha o botão, que chama `GET <path>/export.xlsx` com os
   *  MESMOS filtros da lista. Ausente, não há botão — o backend só publica a rota
   *  para o cadastro que declarou colunas de planilha, e oferecer o clique aqui
   *  renderia 404. Os dois lados são declarados juntos, de propósito. */
  exportPrefix?: string
  /** Espelho de integração: a linha nasce e morre pela sincronização, nunca pela
   *  tela. Esconde "Novo" e a lixeira mesmo de quem tem as permissões — o backend
   *  recusa de qualquer forma; isto evita o clique que só renderia um 403. O lápis
   *  continua: o formulário decide o que ainda é editável. */
  mirror?: boolean
  /** Catálogo consultável, porém mantido por integração. Esconde criação, edição
   *  e exclusão; o backend aplica a mesma regra para acessos diretos. */
  readOnly?: boolean
  /** Permissões (RBAC) que liberam os botões da lista. Ausente = sempre visível
   *  (a segurança real é no backend; isto só evita cliques que dariam 403). */
  createPermission?: string
  /** Sem ela o lápis desaparece. Existia create e delete, e faltava justamente a de
   *  editar: a pessoa abria o formulário, preenchia tudo e só descobria no Salvar. */
  editPermission?: string
  deletePermission?: string
  /** Faixa opcional entre o cabeçalho e os filtros da lista. É um COMPONENTE, para
   *  poder buscar o que exibe (ex.: última sincronização com o Protheus em Pessoas).
   *
   *  Recebe o RECORTE atual da grade, com filtros e busca, para a faixa que precisa
   *  responder ao filtro poder consultá-lo. Quem não precisa simplesmente ignora o
   *  argumento, como as duas faixas do Protheus fazem. */
  Banner?: (props: { params?: ListParams }) => ReactElement | null
  /** Rótulo do contador que acompanha os filtros. Presente, a grade mostra quantas
   *  linhas a consulta ATUAL devolveu, ao lado da busca. A faixa do topo continua
   *  falando do cadastro inteiro; este número responde ao que está filtrado. */
  countLabel?: string
}

/** Hook de filtros vazio (padrão quando o recurso não tem filtros). */
export const NO_FILTERS = (): ResourceFilters => ({})

/** Deriva um config do portal a partir do config do backoffice: reaproveita
 *  schema/columns/Fields/toForm/toBody verbatim, trocando só o `basePath` (rotas
 *  `/portal/...`) e removendo "Nova categoria" e "Importar" (importação em massa
 *  é staff-only). O escopo por cliente é do backend (via JWT); o formulário
 *  esconde o seletor de cliente sozinho (ver CustomerOwnerField).
 *
 *  "Baixar Excel" CONTINUA, ao contrário de Importar: exportar é leitura, e a rota
 *  aplica o mesmo escopo por cliente da listagem. O cliente baixa as linhas que já
 *  vê na tela, não as de outro cliente. */
export function portalConfig<T extends { uuid: string }>(
  base: ResourceConfig<T>,
  overrides: Partial<ResourceConfig<T>> & { basePath: string },
): ResourceConfig<T> {
  // Banner também sai: o que ele mostra hoje é estado de integração interna, que
  // não é assunto do portal do cliente.
  return { ...base, categoryNewPath: undefined, importEntity: undefined, Banner: undefined, ...overrides }
}
